import { ParamList } from '@/collections/params';
import { ParamModel } from '@/models/param';

export const TOPN_CODE = 'topn';
export const TOPN_NAME = 'Show Top';
export const TOPN_ALL = 'All';
export const TOPN_VALUES = ['5', '10', '20', '50', '100', '200', TOPN_ALL];

/**
 * Read the Show Top value out of a segment/report param list.
 * A segment without the param is not limited, so it reads as 'All'.
 */
export function getTopn(params: ParamList | null | undefined): string {
  if (!params || !params.items) {
    return TOPN_ALL;
  }
  for (const item of params.items) {
    if (item.code === TOPN_CODE && item.value && item.value.length > 0) {
      return String(item.value[0]);
    }
  }
  return TOPN_ALL;
}

/**
 * Write the Show Top value into a param list, adding the param when the list
 * does not carry one yet.
 */
export function setTopn(params: ParamList, value: string): ParamList {
  const topnValue = value ? String(value) : TOPN_ALL;
  for (const item of params.items) {
    if (item.code === TOPN_CODE) {
      item.value = [topnValue];
      return params;
    }
  }
  const param = new ParamModel();
  param.mapData({
    name: TOPN_NAME,
    code: TOPN_CODE,
    data_type: 'text',
    value_type: 'single',
    value: [topnValue],
  });
  params.add(param);
  return params;
}

/**
 * Copy a param list so editing a segment never mutates the params of the
 * report it was created from.
 */
export function cloneParams(params: ParamList | null | undefined): ParamList {
  const cloned = new ParamList();
  if (!params || !params.items) {
    return cloned;
  }
  for (const item of params.items) {
    const param = new ParamModel();
    param.mapData(item);
    // mapData only reads the snake_case keys for these two
    param.sortOrder = item.sortOrder;
    param.listValues = item.listValues;
    param.value = Array.isArray(item.value) ? [...item.value] : item.value;
    cloned.add(param);
  }
  return cloned;
}

export function isLimited(topn: string): boolean {
  return !!topn && topn !== TOPN_ALL && !isNaN(Number(topn));
}

export function getSortBy(pageOptions: any): string {
  if (pageOptions && Array.isArray(pageOptions.sortBy) && pageOptions.sortBy.length > 0) {
    return pageOptions.sortBy[0];
  }
  return '';
}

export function getSortDesc(pageOptions: any): boolean {
  if (pageOptions && Array.isArray(pageOptions.sortDesc) && pageOptions.sortDesc.length > 0) {
    return pageOptions.sortDesc[0] !== false;
  }
  return true;
}

/** '[Profit Margin]' -> 'Profit Margin' */
export function formatColumnCode(code: string): string {
  if (!code) {
    return '';
  }
  return code.replace(/^\[/, '').replace(/\]$/, '').replace(/_/g, ' ');
}

export function getColumnLabel(code: string, columns: any[] = []): string {
  if (!code) {
    return '';
  }
  for (const column of columns) {
    if (column && column.code === code) {
      return column.name || formatColumnCode(code);
    }
  }
  return formatColumnCode(code);
}

/** Human readable summary shown in the segment view mode. */
export function describeTopn(topn: string, sortBy: string, sortDesc: boolean, columns: any[] = []): string {
  if (!isLimited(topn)) {
    return 'All matching products';
  }
  const label = getColumnLabel(sortBy, columns);
  if (!label) {
    return `Top ${topn} products`;
  }
  return `Top ${topn} products by ${label} (${sortDesc ? 'highest first' : 'lowest first'})`;
}
